import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type { CensusColumn, ReportView } from "@alm/validation";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportExport } from "@/components/app/documents/report-export";
import {
  periodDescription,
  periodInput,
  periodReady,
  ReportPeriodPicker,
  todayPeriod,
  type ReportPeriod,
} from "@/components/app/documents/report-period";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Skeleton } from "@/components/ui/skeleton";
import { saveFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import {
  periodFromSearch,
  periodSearch,
  periodToSearch,
  type PeriodSearch,
} from "@/lib/facility/report-period-search";
import { optionalString, optionalTrue, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import {
  CensusFilters,
  NO_CENSUS_FILTERS,
  type CensusFilterState,
} from "./-components/census-filters";
import { CensusResidents } from "./-components/census-residents";
import { YearTable } from "@/routes/_authenticated/_financials/census/-components/year-table";
import { MovementCard } from "@/routes/_authenticated/_financials/census/-components/movement-card";
import { CareLevels } from "@/routes/_authenticated/_financials/census/-components/care-levels";

type CensusSearch = PeriodSearch & {
  status?: string;
  institution?: string;
  hospice?: true;
  homeHealth?: true;
};

export const Route = createFileRoute("/_authenticated/_financials/census/")({
  validateSearch: (search: Record<string, unknown>): CensusSearch => ({
    ...periodSearch(search),
    status: optionalString(search.status)?.slice(0, 40) || undefined,
    institution: optionalString(search.institution)?.slice(0, 120) || undefined,
    hospice: optionalTrue(search.hospice),
    homeHealth: optionalTrue(search.homeHealth),
  }),
  component: Census,
});

/* A census is asked for as of today far more often than for a closed period,
   so "today" leads and "current" — the rent roll's word for the same thing —
   is left out rather than offered twice. */
const VIEWS: readonly ReportView[] = [
  "today",
  "month",
  "custom",
  "quarter",
  "year",
  "ytd",
  "yearOverview",
];

/** How full the building is, who arrived and who left. No money: a surveyor is handed this. */
function Census() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<CensusSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const fallback: ReportPeriod = { ...todayPeriod(), view: "today" };
  const period = periodFromSearch(params, fallback);
  const setPeriod = (next: ReportPeriod) => patch(periodToSearch(next, fallback));
  const filters: CensusFilterState = {
    status: params.status ?? NO_CENSUS_FILTERS.status,
    institution: params.institution ?? "",
    onHospice: Boolean(params.hospice),
    onHomeHealth: Boolean(params.homeHealth),
  };
  const setFilters = (next: CensusFilterState) =>
    patch({
      status: next.status === NO_CENSUS_FILTERS.status ? undefined : next.status,
      institution: next.institution || undefined,
      hospice: next.onHospice ? true : undefined,
      homeHealth: next.onHomeHealth ? true : undefined,
    });
  const [columns, setColumns] = useState<CensusColumn[]>([]);
  const canExport = useCan("report:export", facilityId === "all" ? null : facilityId);

  const input = {
    facilityId,
    ...periodInput(period),
    onHospice: filters.onHospice,
    onHomeHealth: filters.onHomeHealth,
    institutionSearch: filters.institution.trim() || undefined,
  };
  const census = useQuery({
    ...orpc.financials.reports.census.queryOptions({ input }),
    enabled: periodReady(period),
  });

  // The file is the resident roster the screen is showing — same period, same
  // filters, same columns — because a census handed to a surveyor that differs
  // from the screen it came off is the one thing worse than no census.
  const download = useMutation(
    orpc.financials.reports.censusExport.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const data = census.data;
  const residents = (data?.residents.activeResidents ?? []).filter(
    (row) => filters.status === "all" || row.status === filters.status,
  );
  const statuses = [...new Set((data?.residents.activeResidents ?? []).map((row) => row.status))];

  return (
    <>
      <PageHeader
        title="Census report"
        meta={
          data
            ? `Occupancy snapshot, ${data.residents.total} ${
                data.residents.total === 1 ? "resident" : "residents"
              } listed, ${data.period.label}`
            : "How full the building is."
        }
        actions={
          // The file needs report:export on top of reading the census; floor
          // staff read it and would only be refused the download.
          canExport ? (
            <ReportExport
              labels={{ csv: "Roster CSV", pdf: "Print roster" }}
              pending={download.isPending}
              onExport={(format) =>
                download.mutate({
                  ...input,
                  format,
                  ...(columns.length > 0 ? { columns } : {}),
                })
              }
            />
          ) : undefined
        }
      />

      <div className="space-y-1">
        <div className="flex flex-wrap items-end gap-3">
          <ReportPeriodPicker period={period} views={VIEWS} onChange={setPeriod} />
          <CensusFilters filters={filters} statuses={statuses} onChange={setFilters} />
        </div>
        <p className="text-muted-foreground text-sm">{periodDescription(period)}</p>
      </div>

      {census.isError ? (
        <ReadFailed
          message="The census could not be loaded."
          error={census.error}
          onRetry={() => void census.refetch()}
          retrying={census.isFetching}
        />
      ) : census.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : !data ? (
        <EmptyState title="No census" description="Choose a building and a period." />
      ) : period.view === "yearOverview" ? (
        <YearTable months={data.monthlyData} />
      ) : (
        <>
          {/* Two occupancy figures on purpose. A bed is occupied by anyone not moved out, including
              a resident in hospital (billed). Who is actually in the building is the smaller number
              a shift is staffed against. */}
          <StatTiles>
            <StatTile
              label="Residents in the building"
              // The API formats the rate, sign included.
              value={data.occupancy.residentsInBuildingRate ?? data.occupancy.residentsInBuilding}
              hint={`${data.occupancy.residentsInBuilding} of ${data.occupancy.totalRooms ?? "-"} rooms, active only`}
            />
            <StatTile
              label="Occupancy"
              value={data.occupancy.occupancyRate ?? data.occupancy.occupiedRooms}
              hint={`${data.occupancy.occupiedRooms} of ${data.occupancy.totalRooms ?? "-"} rooms, incl. out of facility`}
            />
            <StatTile
              // The old tile: residents active in the building, not everyone
              // who passed through the period.
              label="Active residents"
              value={data.occupancy.residentsInBuilding}
              hint={
                data.residents.admissions === 0 && data.residents.discharges === 0
                  ? "No admissions or discharges this period"
                  : `${data.residents.admissions} in, ${data.residents.discharges} out`
              }
            />
            <StatTile
              label="Out of facility, active"
              value={data.occupancy.outOfFacilityActive}
              hint="Still on census"
            />
            <StatTile
              label="Out of facility, hold"
              value={data.occupancy.outOfFacilityHold}
              hint="Bed held"
            />
          </StatTiles>

          <div className="grid items-start gap-4 lg:grid-cols-3">
            <MovementCard
              title="Move-ins"
              empty="No move-ins during this period"
              rows={data.residents.moveIns.map((row) => ({
                id: row.id,
                name: row.name,
                roomNumber: row.roomNumber,
                date: row.admissionDate,
              }))}
            />
            <MovementCard
              title="Move-outs"
              empty="No move-outs during this period"
              rows={data.residents.moveOuts.map((row) => ({
                id: row.id,
                name: row.name,
                roomNumber: row.roomNumber,
                date: row.dischargeDate,
              }))}
            />
            <CareLevels levels={data.careLevels} />
          </div>

          <CensusResidents rows={residents} onColumnsChange={setColumns} />
        </>
      )}
    </>
  );
}
